import { useMemo, useState } from "react";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { SEMANTIC } from "assets/theme/avocadoBrand";

/**
 * Nhóm trường form: chỉ mở/đóng khi bấm tiêu đề + Divider.
 * Không tự đóng / tự nhảy nhóm.
 */
function TieuDeNhomForm({ tieuDe, moTa, hoanThanh, mo, khoa, onToggle, children }) {
  return (
    <Grid item xs={12}>
      <MDBox
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1}
        mt={0.5}
        mb={0.5}
        onClick={khoa ? undefined : onToggle}
        onKeyDown={
          khoa
            ? undefined
            : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle();
                }
              }
        }
        role="button"
        tabIndex={khoa ? -1 : 0}
        aria-expanded={mo}
        aria-disabled={khoa}
        sx={{
          cursor: khoa ? "not-allowed" : "pointer",
          userSelect: "none",
          borderRadius: 1,
          px: 0.5,
          mx: -0.5,
          opacity: khoa ? 0.55 : 1,
          "&:hover": khoa ? undefined : { backgroundColor: "rgba(0,0,0,0.03)" },
        }}
      >
        <MDBox flex={1} minWidth={0}>
          <MDTypography
            variant="caption"
            color="text"
            textTransform="uppercase"
            fontWeight="bold"
            letterSpacing="0.06em"
            display="block"
          >
            {tieuDe}
          </MDTypography>
          {(khoa || moTa) && (
            <MDTypography
              variant="caption"
              color="text"
              display="block"
              mt={0.25}
              sx={{ opacity: 0.85 }}
            >
              {khoa ? "Hoàn thành mục phía trên trước" : moTa}
            </MDTypography>
          )}
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={0.25} flexShrink={0} mt={0.1}>
          {khoa ? (
            <LockOutlinedIcon
              fontSize="small"
              sx={{ color: "text.secondary" }}
              titleAccess="Khóa"
            />
          ) : null}
          {!khoa && hoanThanh === true ? (
            <CheckCircleIcon
              fontSize="small"
              sx={{ color: SEMANTIC.success }}
              titleAccess="Đã điền đủ"
            />
          ) : null}
          {!khoa && hoanThanh === false ? (
            <CancelIcon
              fontSize="small"
              sx={{ color: SEMANTIC.error }}
              titleAccess="Chưa điền đủ"
            />
          ) : null}
          <IconButton
            size="small"
            tabIndex={-1}
            aria-hidden
            disabled={khoa}
            sx={{
              p: 0.25,
              transform: mo ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </MDBox>
      </MDBox>
      <Divider
        sx={{
          mb: 0.5,
          borderColor: !khoa && hoanThanh === true ? SEMANTIC.success : undefined,
          opacity: !khoa && hoanThanh === true ? 0.45 : 1,
          ...(!khoa && hoanThanh === false ? { borderColor: SEMANTIC.error, opacity: 0.35 } : {}),
        }}
      />
      <Collapse in={mo} timeout="auto">
        <Grid container spacing={2} pt={0.5} pb={1}>
          {children}
        </Grid>
      </Collapse>
    </Grid>
  );
}

TieuDeNhomForm.propTypes = {
  tieuDe: PropTypes.node.isRequired,
  moTa: PropTypes.string,
  hoanThanh: PropTypes.bool,
  mo: PropTypes.bool.isRequired,
  khoa: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

TieuDeNhomForm.defaultProps = {
  moTa: undefined,
  hoanThanh: null,
  khoa: false,
};

export default TieuDeNhomForm;

/** Chuỗi có nội dung (bỏ khoảng trắng). */
export function coNoiDung(giaTri) {
  return String(giaTri ?? "").trim() !== "";
}

export function layNhomDangNhap(thuTu, trangThai) {
  return thuTu.find((id) => trangThai[id] === false) ?? "";
}

/**
 * Điền từ trên xuống, chỉ mở/đóng bằng Divider:
 * - Không tự đóng / tự nhảy khi field vừa đủ.
 * - Nhóm dưới khóa đến khi nhóm trên đủ (tick xanh).
 * - Nhóm tùy chọn: mở bằng Divider; đóng Divider = đã xem xong (mở khóa mục sau).
 * - Nhóm bắt buộc chưa đủ: không cho đóng bằng Divider.
 */
export function useNhomFormMo(thuTu, trangThai, nhomTuyChon = [], daXacNhanTuyChonBanDau = false) {
  const [xacNhanTuyChon, setXacNhanTuyChon] = useState(() =>
    daXacNhanTuyChonBanDau ? Object.fromEntries(nhomTuyChon.map((id) => [id, true])) : {}
  );
  const [nhomMo, setNhomMo] = useState(thuTu[0] ?? "");

  const trangThaiHieuLuc = useMemo(() => {
    const ketQua = { ...trangThai };
    nhomTuyChon.forEach((id) => {
      ketQua[id] = !!xacNhanTuyChon[id];
    });
    return ketQua;
  }, [trangThai, nhomTuyChon, xacNhanTuyChon]);

  const nhomDangNhap = layNhomDangNhap(thuTu, trangThaiHieuLuc);

  function indexNhom(id) {
    return thuTu.indexOf(id);
  }

  function laTuyChon(id) {
    return nhomTuyChon.includes(id);
  }

  function khoaNhom(id) {
    if (!nhomDangNhap) return false;
    return indexNhom(id) > indexNhom(nhomDangNhap);
  }

  function xacNhanTuyChonNeuCan(id) {
    if (!id || !laTuyChon(id)) return;
    setXacNhanTuyChon((cu) => (cu[id] ? cu : { ...cu, [id]: true }));
  }

  function toggleNhom(id) {
    if (khoaNhom(id)) return;

    // Đóng nhóm đang mở — chỉ khi đủ (bắt buộc) hoặc là tùy chọn.
    if (nhomMo === id) {
      if (trangThaiHieuLuc[id] === false && !laTuyChon(id)) return;
      xacNhanTuyChonNeuCan(id);
      setNhomMo("");
      return;
    }

    // Mở nhóm khác: xác nhận tùy chọn đang mở (nếu có).
    xacNhanTuyChonNeuCan(nhomMo);

    // Không bỏ nhóm bắt buộc chưa đủ để nhảy xuống dưới.
    if (
      nhomMo &&
      trangThaiHieuLuc[nhomMo] === false &&
      !laTuyChon(nhomMo) &&
      indexNhom(id) > indexNhom(nhomMo)
    ) {
      return;
    }

    setNhomMo(id);
  }

  return {
    nhomMo,
    toggleNhom,
    khoaNhom,
    nhomDangNhap,
    trangThaiHieuLuc,
  };
}
